import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, Calendar, MapPin, Users, Hash, Phone, Plane, FileText } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const STATUS_COLOR = {
  confirmed: "text-[#34D399] border-[#34D399]/40",
  pending: "text-[#FBBF24] border-[#FBBF24]/40",
  cancelled: "text-[#F87171] border-[#F87171]/40",
  completed: "text-white/60 border-white/20",
};

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setBooking(data);
    } catch (_e) {
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const cancel = async () => {
    if (!window.confirm("Cancel this reservation?")) return;
    setCancelling(true);
    try {
      const { data } = await api.patch(`/bookings/${id}/cancel`);
      setBooking(data);
      toast.success("Reservation cancelled.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-32 px-6 text-white/60">Loading…</div>;
  if (!booking) return <div className="min-h-screen pt-32 px-6 text-white/60">Reservation not found.</div>;

  const canCancel = booking.status !== "cancelled" && booking.status !== "completed";

  return (
    <div data-testid="trip-detail-page" className="min-h-screen bg-[#0A0A0A] pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <button
          data-testid="back-to-trips-btn"
          onClick={() => navigate("/trips")}
          className="text-xs uppercase tracking-[0.3em] text-white/60 hover:text-[#C5A059] inline-flex items-center gap-2 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Trips
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Reservation</div>
            <h1 className="font-mono-display text-3xl text-white mt-2" data-testid="detail-code">{booking.confirmation_code}</h1>
          </div>
          <span data-testid="detail-status" className={`inline-block text-[10px] uppercase tracking-wider px-3 py-1.5 border ${STATUS_COLOR[booking.status] || ""}`}>
            {booking.status}
          </span>
        </div>

        <div className="border border-white/10 bg-[#141414] mt-8 grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-5 bg-[#0A0A0A] aspect-[5/4] md:aspect-auto overflow-hidden">
            {/* image is referenced via vehicle id by name only — provide placeholder using vehicle class name */}
            <VehicleImage vehicleId={booking.vehicle_id} alt={booking.vehicle_name} />
          </div>
          <div className="md:col-span-7 p-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{booking.vehicle_class}</div>
            <div className="font-heading text-3xl text-white mt-1">{booking.vehicle_name}</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mt-8 text-sm">
              <Detail icon={Calendar} label="Pickup" value={format(new Date(booking.pickup_datetime), "PPPp")} />
              <Detail icon={Hash} label="Trip Type" value={booking.ride_type.replace("-", " ")} />
              <Detail icon={MapPin} label="Pickup Address" value={booking.pickup_address} />
              {booking.dropoff_address && <Detail icon={MapPin} label="Drop-off" value={booking.dropoff_address} />}
              <Detail icon={Users} label="Passengers" value={booking.passengers} />
              {booking.contact_phone && <Detail icon={Phone} label="Contact" value={booking.contact_phone} />}
              {booking.flight_number && <Detail icon={Plane} label="Flight" value={booking.flight_number} />}
            </div>

            {booking.notes && (
              <div className="mt-6 border-t border-white/10 pt-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" /> Special Requests
                </div>
                <p className="text-white/80 text-sm mt-2 leading-relaxed">{booking.notes}</p>
              </div>
            )}

            <div className="mt-8 border-t border-white/10 pt-6 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Total Fare</div>
                <div className="font-heading text-4xl text-white mt-1">${booking.estimated_fare.toFixed(2)}</div>
              </div>
              {canCancel && (
                <button
                  data-testid="cancel-booking-btn"
                  onClick={cancel}
                  disabled={cancelling}
                  className="border border-[#F87171]/50 text-[#F87171] hover:bg-[#F87171] hover:text-white transition-colors px-6 py-3 text-xs uppercase tracking-[0.3em] disabled:opacity-50"
                >
                  {cancelling ? "Cancelling…" : "Cancel Reservation"}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <Link
            to="/book"
            className="border border-white/20 hover:border-white text-white px-8 py-3 text-xs uppercase tracking-[0.3em] transition-colors"
          >
            Book Another Ride
          </Link>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3">
      <Icon className="w-4 h-4 text-[#C5A059] mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
        <div className="text-white capitalize mt-0.5">{value}</div>
      </div>
    </div>
  );
}

const VEHICLE_IMAGES = {
  "sedan": "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/3f0b448cde6206bf0667628635dc0a786a21db8e92080f7515faccd4efa5dd51.png",
  "suv": "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/a248946b1a97ba4db53834de226b08e879fc3f0be6f9dbc2fad3d937e6f60748.png",
  "business-sedan": "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/3f0b448cde6206bf0667628635dc0a786a21db8e92080f7515faccd4efa5dd51.png",
  "executive-suv": "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/a248946b1a97ba4db53834de226b08e879fc3f0be6f9dbc2fad3d937e6f60748.png",
  "luxury-sedan": "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/122cf768b9e93deba18a0b25e2c22296ad8197a68904f4ee3b4c31b436b2e246.png",
  "stretch-limo": "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/1d72706b4bfa4381e046b28bedb3f9e4802109031d08d4a8f1e59fc4e8b7301a.png",
};
function VehicleImage({ vehicleId, alt }) {
  const src = VEHICLE_IMAGES[vehicleId];
  return src ? <img src={src} alt={alt} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#0A0A0A]" />;
}
